import { parseDateOnly } from '../../utils/dateUtils';
import { isPaidBookingPaymentStatus } from '../../utils/bookingPayment';

export const ORGANIZER_BOOKING_TABS = {
  UPCOMING: 'upcoming',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

export const classifyBookingTab = (booking) => {
  const status = String(booking?.status || '').toLowerCase();
  if (status === 'cancelled') return ORGANIZER_BOOKING_TABS.CANCELLED;
  if (status === 'completed') return ORGANIZER_BOOKING_TABS.COMPLETED;
  return ORGANIZER_BOOKING_TABS.UPCOMING;
};

export const getOrganizerBookingBadge = (booking) => {
  const status = String(booking?.status || '').toLowerCase();
  if (status === 'confirmed' || status === 'upcoming') {
    return { label: 'Confirmed', variant: 'confirmed' };
  }
  if (status === 'cancelled') {
    return { label: 'Cancelled', variant: 'cancelled' };
  }
  if (status === 'completed') {
    return { label: 'Completed', variant: 'completed' };
  }
  return { label: 'Pending confirmation', variant: 'pending' };
};

export const getOrganizerPaymentLabel = (booking, venueApproved) => {
  if (!venueApproved) return { label: 'Not paid', variant: 'unpaid' };
  if (isPaidBookingPaymentStatus(booking?.paymentStatus)) {
    return { label: 'Paid', variant: 'paid' };
  }
  return { label: 'Not paid', variant: 'unpaid' };
};

export const formatOrganizerCurrency = (amount) => {
  const numeric = Number(amount || 0);
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 2,
  }).format(Number.isFinite(numeric) ? numeric : 0);
};

export const formatOrganizerDate = (dateString) =>
  parseDateOnly(dateString)
    ? parseDateOnly(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : 'Date TBD';

export const formatOrganizerTimeRange = (startTime, endTime) => {
  const start = startTime ? startTime.substring(0, 5) : '';
  const end = endTime ? endTime.substring(0, 5) : '';
  if (start && end) return `${start} - ${end}`;
  return start || end || 'Time TBD';
};

export const computeBriefCountdown = (createdAt, hours = 24) => {
  if (!createdAt) return null;
  const start = new Date(createdAt).getTime();
  if (Number.isNaN(start)) return null;
  const deadline = start + hours * 60 * 60 * 1000;
  const remainingMs = deadline - Date.now();
  if (remainingMs <= 0) return '00:00:00';
  const totalSeconds = Math.floor(remainingMs / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
};

export const computeProfileCompletion = ({
  currentUser,
  userProfile,
  hasBookingOrBrief = false,
  hasVenueList = false,
}) => {
  const items = [
    {
      id: 'google',
      label: 'Connect Google account',
      done: Boolean(
        currentUser?.providerData?.some((p) => p?.providerId === 'google.com')
      ),
    },
    {
      id: 'email',
      label: 'Verify email ID',
      done: Boolean(currentUser?.emailVerified),
    },
    {
      id: 'phone',
      label: 'Add and verify phone no.',
      done: Boolean(userProfile?.phone),
    },
    {
      id: 'photo',
      label: 'Add a profile picture',
      done: Boolean(currentUser?.photoURL),
    },
    {
      id: 'facebook',
      label: 'Connect Facebook account',
      done: Boolean(
        currentUser?.providerData?.some((p) => p?.providerId === 'facebook.com')
      ),
    },
    {
      id: 'preference',
      label: 'Set your venue preference',
      done: Boolean(userProfile?.venue_preference || userProfile?.venuePreference),
    },
    {
      id: 'shortlist',
      label: 'Create your first venue shortlist',
      done: hasVenueList,
    },
    {
      id: 'booking',
      label: 'Make your first booking request',
      done: hasBookingOrBrief,
    },
  ];

  const doneCount = items.filter((item) => item.done).length;
  const percent = Math.round((doneCount / items.length) * 100);

  return { items, percent };
};
